import { fDate } from '@/utils/dayjs'
import { useState, useRef } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { fNumber } from '@/utils/format-number'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  MoreHorizontal,
  Edit,
  Trash2,
  Music,
  Play,
  Pause,
  Check,
  Eye,
  Ban,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'

import type { TrendSound, TrendSoundType, TrendSoundStatus } from './types'

import { TREND_SOUND_TYPE_OPTIONS } from './types'

interface TrendSoundTableProps {
  trendSounds: TrendSound[]
  loading: boolean
  onEdit?: (trendSound: TrendSound) => void
  onDelete?: (trendSound: TrendSound) => void
  onStatusChange?: (trendSound: TrendSound, status: TrendSoundStatus) => void
}

const getTypeLabel = (type: TrendSoundType) => {
  const option = TREND_SOUND_TYPE_OPTIONS.find((opt) => opt.value === type)
  return option?.label || type
}

const getStatusBadge = (status: TrendSoundStatus) => {
  switch (status) {
    case 'active':
      return <Badge className="bg-green-500 text-background">เปิดใช้งาน</Badge>
    case 'hidden':
      return <Badge className="bg-gray-500 text-background">ซ่อน</Badge>
    case 'deleted':
      return <Badge className="bg-red-500 text-background">ลบแล้ว</Badge>
    case 'banned':
      return <Badge className="bg-orange-500 text-background">ถูกแบน</Badge>
    default:
      return <Badge>{status}</Badge>
  }
}

const formatDuration = (seconds: number) => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export default function TrendSoundTable({
  trendSounds,
  loading,
  onEdit,
  onDelete,
  onStatusChange,
}: TrendSoundTableProps) {
  const [playingId, setPlayingId] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const handlePlayPause = (trendSound: TrendSound) => {
    if (playingId === trendSound.id) {
      // Pause current
      audioRef.current?.pause()
      setPlayingId(null)
    } else {
      // Play new
      if (audioRef.current) {
        audioRef.current.pause()
      }
      audioRef.current = new Audio(trendSound.audioUrl)
      audioRef.current.play()
      audioRef.current.onended = () => setPlayingId(null)
      setPlayingId(trendSound.id)
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">รูปปก</TableHead>
                <TableHead>ชื่อเสียง</TableHead>
                <TableHead className="w-24">ประเภท</TableHead>
                <TableHead className="w-24">สถานะ</TableHead>
                <TableHead className="w-20">ระยะเวลา</TableHead>
                <TableHead className="w-24">การใช้งาน</TableHead>
                {/* <TableHead className="w-24">ถูกใจ</TableHead> */}
                <TableHead className="w-32">วันที่สร้าง</TableHead>
                <TableHead className="w-16">การจัดการ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <div className="w-12 h-12 bg-gray-200 rounded-lg animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-16 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-16 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  {/* <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell> */}
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    )
  }

  if (trendSounds.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center">
        <Music className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-base text-mute">ไม่พบข้อมูลเสียงเทรนด์</p>
      </div>
    )
  }

  return (
    <Card className="p-0">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">รูปปก</TableHead>
              <TableHead>ชื่อเสียง</TableHead>
              <TableHead className="w-24">ประเภท</TableHead>
              <TableHead className="w-24">สถานะ</TableHead>
              <TableHead className="w-20">ระยะเวลา</TableHead>
              <TableHead className="w-24">การใช้งาน</TableHead>
              {/* <TableHead className="w-24">ถูกใจ</TableHead> */}
              <TableHead className="w-32">วันที่สร้าง</TableHead>
              <TableHead className="w-16">การจัดการ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {trendSounds.map((trendSound) => (
              <TableRow key={trendSound.id}>
                <TableCell>
                  <div
                    className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 relative cursor-pointer group"
                    onClick={() => handlePlayPause(trendSound)}
                  >
                    {trendSound.coverUrl ? (
                      <img
                        src={trendSound.coverUrl}
                        alt={trendSound.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <Music className="h-5 w-5 text-gray-400" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      {playingId === trendSound.id ? (
                        <Pause className="h-5 w-5 text-white" />
                      ) : (
                        <Play className="h-5 w-5 text-white" />
                      )}
                    </div>
                    {playingId === trendSound.id && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <Pause className="h-5 w-5 text-white" />
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <p className="text-base font-medium text-black line-clamp-1">
                      {trendSound.title}
                    </p>
                    {trendSound.artist && (
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {trendSound.artist}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {getTypeLabel(trendSound.type)}
                  </Badge>
                </TableCell>
                <TableCell>{getStatusBadge(trendSound.status)}</TableCell>
                <TableCell>
                  <p className="text-sm text-black">
                    {formatDuration(trendSound.duration)}
                  </p>
                </TableCell>
                <TableCell>
                  <p className="text-sm text-black">
                    {fNumber(trendSound.usageCount)}
                  </p>
                </TableCell>
                {/* <TableCell>
                  <p className="text-sm text-black">
                    {fNumber(trendSound.likeCount)}
                  </p>
                </TableCell> */}
                <TableCell>
                  <p className="text-sm text-mute">
                    {fDate(trendSound.createdAt)}
                  </p>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {onEdit && (
                        <DropdownMenuItem onClick={() => onEdit(trendSound)}>
                          <Edit className="mr-2 h-4 w-4" />
                          แก้ไข
                        </DropdownMenuItem>
                      )}
                      {onStatusChange && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => onStatusChange(trendSound, 'active')}
                            disabled={trendSound.status === 'active'}
                          >
                            <Check className="mr-2 h-4 w-4 text-green-500" />
                            เปิดใช้งาน
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onStatusChange(trendSound, 'hidden')}
                            disabled={trendSound.status === 'hidden'}
                          >
                            <Eye className="mr-2 h-4 w-4 text-gray-500" />
                            ซ่อน
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onStatusChange(trendSound, 'banned')}
                            disabled={trendSound.status === 'banned'}
                          >
                            <Ban className="mr-2 h-4 w-4 text-orange-500" />
                            แบน
                          </DropdownMenuItem>
                        </>
                      )}
                      {onDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => onDelete(trendSound)}
                            className="text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            ลบ
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
